import React, {useEffect, useId, useMemo, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import Icon from '@/shared/components/Icon';
import clsx from 'clsx';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useAppSelector} from '@/shared/hooks/redux';
import {selectCurrentPatient} from '@/slices/patientSlice';
import {Link} from 'react-router-dom';
import {useSubscription} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {useIsDashboardModuleEnabled} from '@/modules/patient-dashboard/hooks/useDashboardModules';
import {apiClient} from '@/shared/services/apiClient';
import {resolveMediaUrl} from '@/shared/utils/mediaUrl';
import {formatBirthdate} from '@/shared/utils/formatBirthdate';
import SectionHeader from '@/shared/components/SectionHeader';
import {calculateBMI} from '../utils/bmi';
import {useLatestBiometrics, useBiometricHistory} from '../hooks/useBiometrics';
import HealthProfileEditModal from './HealthProfileEditModal';
import ManageMembershipModal from './ManageMembershipModal';

interface HealthProfileData {
    heightInches?: number;
    weightLbs?: number;
    waistInches?: number;
    bpSystolic?: number;
    bpDiastolic?: number;
    medicalHistory?: string[];
    allergies?: string[];
    medicalConditions?: string[];
    profilePhotoUrl?: string | null;
    // Live demographics off the users row (the /api/user/health-profile
    // endpoint now returns these so the card reflects the current DB value
    // rather than a possibly-stale cached auth userInfo).
    sex?: string | null;
    birthday?: string | null;
}

interface HealthProfileCardProps {
    /**
     * @deprecated Header is always rendered now. Prop retained so existing
     * call sites keep compiling; remove in a follow-up once DashboardView
     * stops passing it.
     */
    showExternalHeader?: boolean;
}

function formatHeight(inches: number): string {
    return `${Math.round(inches)} in`;
}

// Every Biometric Data row keeps its label even with nothing on file — a
// missing value shows this dash rather than dropping the row, so the card
// reads the same for a brand-new patient as for a fully measured one.
const DASH = '—';

function formatMeasurement(value: number | undefined, render: (v: number) => string): string {
    return value ? render(value) : DASH;
}

// Blood pressure is one row carrying two readings, so a missing half dashes
// in place; only a completely empty pair collapses to a bare dash.
function formatBloodPressure(systolic?: number, diastolic?: number): string {
    if (!systolic && !diastolic) return DASH;
    return `${systolic || DASH}/${diastolic || DASH} mmHg`;
}

// Measurements whose recency the "Biometric Data (date)" title reports —
// everything rendered in that region, including the BMI pill.
const TITLE_DATE_MEASURES = ['height', 'weight', 'waist', 'bp_systolic', 'bp_diastolic', 'bmi'];

// One row in the "Your Biometric History" column. Sparkline render is gated
// per-metric on availability (>=2 readings). Blood Pressure is a single row
// carrying both series — `secondary` holds diastolic so systolic/diastolic
// share one chart instead of taking two rows.
interface TrendItem {
    key: string;
    label: string;
    values: number[];
    secondary?: number[];
}

function getInitials(first: string, last: string): string {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

// Mirror of the server rules (photo.ts) so obvious problems fail fast client-side.
const PHOTO_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const PHOTO_MAX_BYTES = 4 * 1024 * 1024;

function formatTrendValue(v: number): string {
    return Number.isInteger(v) ? `${v}` : v.toFixed(1).replace(/\.0$/, '');
}

// ── Sparkline ──────────────────────────────────────────────────────────
// Small inline SVG sparkline matching the v5 design. Values are rendered
// above each data point; smooth horizontal-cubic Bezier between points
// keeps the curve gentle even with only 3 readings.

interface SparklineProps {
    values: number[];
    // Optional second series drawn on the same y-scale (diastolic for BP).
    // When present, point labels read "primary/secondary" (e.g. "120/80").
    secondary?: number[];
    width?: number;
    height?: number;
}

const PRIMARY_STROKE = '#013B4B'; // navy — systolic / single series
const SECONDARY_STROKE = '#5FA8BC'; // lighter teal — diastolic

const Sparkline: React.FC<SparklineProps> = ({values, secondary, width = 260, height = 52}) => {
    // useId must run on every render, so call it before any early return.
    const gradId = `sparkline-grad-${useId()}`;
    if (values.length < 1) return null;

    const hasSecondary = Array.isArray(secondary) && secondary.length === values.length;
    // One reading → render a flat line with a single marker rather than hiding
    // the chart, so the row still reads as a graph (just no trend yet).
    const single = values.length === 1;

    const padTop = 18;
    const padBottom = 8;
    const padX = 16;

    // Shared y-scale spans both series so systolic and diastolic stay in
    // proportion to one another.
    const scaleVals = hasSecondary ? [...values, ...secondary!] : values;
    const min = Math.min(...scaleVals);
    const max = Math.max(...scaleVals);
    const range = max - min || 1;

    const innerW = width - padX * 2;
    const innerH = height - padTop - padBottom;

    const toPoint = (v: number, i: number) => ({
        // Single reading: center the marker so its value label has room on both
        // sides and never clips the frame edge.
        x: single ? width / 2 : padX + (i * innerW) / (values.length - 1),
        // A lone single-series reading centers vertically (flat line); anything
        // with a real scale (multi-point, or a single BP pair) uses the value.
        y:
            single && !hasSecondary
                ? padTop + innerH / 2
                : padTop + innerH - ((v - min) / range) * innerH * 0.7 - innerH * 0.15,
        value: v,
    });
    const points = values.map(toPoint);
    const points2 = hasSecondary ? secondary!.map(toPoint) : null;

    // Container inset so the chart frame is visible around the area+curve.
    const frameInset = 1;
    const leftX = frameInset; // left border
    const rightX = width - frameInset; // right border
    const baseY = height - frameInset; // chart bottom — area + vertical-line foot

    // Curve path: a flat lead-in from the left border to the first marker, a
    // gentle cubic between markers, then a flat lead-out to the right border —
    // so the line meets both borders while the markers stay inset.
    const buildLine = (pts: Array<{x: number; y: number}>) => {
        // One point → a flat line spanning the full width at that value.
        if (pts.length === 1) return `M ${leftX},${pts[0].y} L ${rightX},${pts[0].y}`;
        let d = `M ${leftX},${pts[0].y} L ${pts[0].x},${pts[0].y}`;
        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[i];
            const p1 = pts[i + 1];
            const mx = (p0.x + p1.x) / 2;
            d += ` C ${mx},${p0.y} ${mx},${p1.y} ${p1.x},${p1.y}`;
        }
        d += ` L ${rightX},${pts[pts.length - 1].y}`;
        return d;
    };

    const line = buildLine(points);
    const line2 = points2 ? buildLine(points2) : null;
    const area = `${line} L ${rightX},${baseY} L ${leftX},${baseY} Z`;

    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" preserveAspectRatio="none">
            <defs>
                {/* Cyan area fill — sampled from source design at ~35%
                    opacity of #70D4E0 (mld-lab-trend) on white. */}
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#70D4E0" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#70D4E0" stopOpacity={0.03} />
                </linearGradient>
                {/* Clip the area fill to inside the rounded chart frame. */}
                <clipPath id={`${gradId}-clip`}>
                    <rect
                        x={frameInset}
                        y={frameInset}
                        width={width - frameInset * 2}
                        height={height - frameInset * 2}
                        rx={6}
                    />
                </clipPath>
            </defs>
            {/* Chart frame — rounded gray-bordered container around curve. */}
            <rect
                x={frameInset + 0.5}
                y={frameInset + 0.5}
                width={width - frameInset * 2 - 1}
                height={height - frameInset * 2 - 1}
                rx={6}
                fill="white"
                stroke="#E5E7EB"
                strokeWidth={1}
            />
            {/* Vertical line at each marker, dropping from the point DOWN to the
                chart bottom (never above the curve), matching Result History. */}
            <g clipPath={`url(#${gradId}-clip)`}>
                {points.map((p) => (
                    <line key={`grid-${p.x}`} x1={p.x} y1={p.y} x2={p.x} y2={baseY} stroke="#E5E7EB" strokeWidth={1} />
                ))}
                <path d={area} fill={`url(#${gradId})`} />
                {line2 && (
                    <path d={line2} fill="none" stroke={SECONDARY_STROKE} strokeWidth={1.5} strokeLinecap="round" />
                )}
                <path d={line} fill="none" stroke={PRIMARY_STROKE} strokeWidth={1.5} strokeLinecap="round" />
            </g>
            {/* Secondary (diastolic) dots — drawn unlabeled; the combined label
                rides above the primary point. */}
            {points2 &&
                points2.map((p) => <circle key={`s-${p.x}`} cx={p.x} cy={p.y} r={2.5} fill={SECONDARY_STROKE} />)}
            {points.map((p, i) => (
                <g key={p.x}>
                    <circle cx={p.x} cy={p.y} r={3} fill={PRIMARY_STROKE} />
                    <text
                        x={p.x}
                        y={(points2 ? Math.min(p.y, points2[i].y) : p.y) - 11}
                        textAnchor="middle"
                        fontSize="12"
                        fontFamily="Poppins, system-ui, sans-serif"
                        fontWeight={500}
                        fill={PRIMARY_STROKE}>
                        {points2
                            ? `${formatTrendValue(p.value)}/${formatTrendValue(points2[i].value)}`
                            : formatTrendValue(p.value)}
                    </text>
                </g>
            ))}
        </svg>
    );
};

// ── Biometric history face ─────────────────────────────────────────────
// One side of the flip card: a title + an availability note + the scrollable
// list of sparkline rows. Front = clinically-validated readings, back =
// patient self-reported entries.

const BiometricFace: React.FC<{
    title: string;
    note: React.ReactNode;
    trends: TrendItem[];
    emptyText: string;
}> = ({title, note, trends, emptyText}) => (
    <>
        {/* Title + status badge share one row; pr-8 reserves space so the
            absolute flip toggle (top-right) never overlaps the text. */}
        <div className="flex items-center gap-2 pr-8 text-mld-text-primary">
            <Icon name="chart-column" className="h-5 w-5 shrink-0" />
            <span className="text-base font-medium">{title}</span>
            {note}
        </div>
        {trends.length > 0 ? (
            <div className="mt-2 max-h-[150px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-mld-panel-border scrollbar-track-transparent">
                <div className="space-y-1">
                    {trends.map((t) => (
                        <div key={t.key} className="grid grid-cols-[68px_1fr] items-center gap-x-3 min-h-[68px]">
                            <div className="text-xs text-gray-500">{t.label}</div>
                            <Sparkline values={t.values} secondary={t.secondary} />
                        </div>
                    ))}
                </div>
            </div>
        ) : (
            <p className="mt-3 text-xs text-gray-400">{emptyText}</p>
        )}
    </>
);

// ── Track Data ─────────────────────────────────────────────────────────
// Opens the biometric edit modal. Rendered in two places — the biometric-history
// tile, and the Biometric Data header when the patient has nothing on file yet —
// so the markup lives here rather than being copied.

const TrackDataButton: React.FC<{onClick: () => void; className?: string}> = ({onClick, className}) => (
    <button
        type="button"
        onClick={onClick}
        className={clsx(
            'inline-flex items-center gap-1.5 text-sm font-medium text-mld-text-primary hover:text-mld-primary transition-colors',
            className
        )}>
        <Icon name="pencil" className="h-3.5 w-3.5" />
        Track Data
    </button>
);

// ── Avatar ─────────────────────────────────────────────────────────────

const Avatar: React.FC<{
    photoUrl?: string | null;
    initials: string;
    canEdit?: boolean;
    busy?: boolean;
    onSelectFile?: (file: File) => void;
    onRemove?: () => void;
}> = ({photoUrl, initials, canEdit, busy, onSelectFile, onRemove}) => {
    const [imgFailed, setImgFailed] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const showPhoto = Boolean(photoUrl) && !imgFailed;

    // A new URL (fresh upload or newly minted signed link) gets a fresh chance.
    useEffect(() => setImgFailed(false), [photoUrl]);

    const openPicker = () => fileInputRef.current?.click();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Reset so re-picking the same file fires change again.
        e.target.value = '';
        if (file) onSelectFile?.(file);
    };

    return (
        <div className="relative my-auto shrink-0">
            <div className="h-[160px] w-[160px] rounded-full overflow-hidden bg-[#DFDCED] flex items-center justify-center">
                {showPhoto ? (
                    <img
                        src={photoUrl ?? undefined}
                        alt={initials}
                        data-testid="profile-photo"
                        className="h-full w-full object-cover"
                        onError={() => setImgFailed(true)}
                    />
                ) : (
                    <span className="text-3xl font-semibold text-mld-text-primary">{initials}</span>
                )}
            </div>
            {canEdit && (
                <>
                    <button
                        type="button"
                        onClick={() => (showPhoto ? setMenuOpen((open) => !open) : openPicker())}
                        disabled={busy}
                        aria-label="Change profile picture"
                        className="absolute bottom-1 right-1 flex h-9 w-9 items-center justify-center rounded-full bg-mld-primary text-mld-text-primary border-2 border-white shadow-sm hover:brightness-95 disabled:opacity-60">
                        <Icon name={busy ? 'spinner' : 'camera'} spin={busy} className="h-4 w-4" />
                    </button>
                    {menuOpen && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                            <div className="absolute -bottom-2 left-1/2 z-20 w-44 -translate-x-1/2 translate-y-full rounded-lg border border-mld-panel-border bg-white py-1 shadow-lg">
                                <button
                                    type="button"
                                    className="block w-full px-4 py-2 text-left text-sm text-mld-text-primary hover:bg-mld-background"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        openPicker();
                                    }}>
                                    Change photo
                                </button>
                                <button
                                    type="button"
                                    className="block w-full px-4 py-2 text-left text-sm text-mld-danger hover:bg-mld-background"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onRemove?.();
                                    }}>
                                    Remove photo
                                </button>
                            </div>
                        </>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={handleFileChange}
                    />
                </>
            )}
        </div>
    );
};

// ── Main card ──────────────────────────────────────────────────────────

const HealthProfileCard: React.FC<HealthProfileCardProps> = () => {
    const {user} = useAuth();
    const currentPatient = useAppSelector(selectCurrentPatient);
    const isSubscribed = useSubscription();
    const isModuleEnabled = useIsDashboardModuleEnabled();
    // Free tier is the absence of a membership, but only worth saying on orgs
    // that actually sell one.
    const showFreeTier = !isSubscribed && isModuleEnabled('patient.mylabs_plus');
    const [isEditing, setIsEditing] = useState(false);
    const [isManagingMembership, setIsManagingMembership] = useState(false);
    // Region 3 flips between clinically-validated history (front) and the
    // patient's own self-reported entries (back).
    const [isFlipped, setIsFlipped] = useState(false);

    const {data: profile} = useQuery<HealthProfileData>({
        queryKey: ['health-profile'],
        queryFn: async () => {
            const res = await apiClient.get<{data: HealthProfileData}>('/api/user/health-profile');
            return res.data;
        },
        enabled: !!user?.isLoggedIn,
    });

    const queryClient = useQueryClient();
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);

    // Photo belongs to the logged-in account; hide the feature when the card
    // is showing a selected dependent so their name never pairs with the
    // account holder's face.
    const isSelf = !currentPatient?.id || currentPatient.id === user?.userInfo?.id;

    const photoErrorMessage = (err: unknown, fallback: string): string => {
        const data = (err as {data?: {error?: string}})?.data;
        if (data?.error) return data.error;
        return err instanceof Error && err.message ? err.message : fallback;
    };

    const clearPhotoPreview = () => {
        setPhotoPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
    };

    const uploadPhoto = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('photo', file);
            return apiClient.post('/api/user/health-profile/photo', formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            });
        },
        onSuccess: async () => {
            // Refetch BEFORE dropping the local preview so the card never
            // flashes back to initials between preview and fresh signed URL.
            await queryClient.invalidateQueries({queryKey: ['health-profile']});
            toast.success('Profile photo updated');
        },
        onError: (err) => {
            toast.error(photoErrorMessage(err, 'Failed to upload profile photo'));
        },
        onSettled: clearPhotoPreview,
    });

    const removePhoto = useMutation({
        mutationFn: async () => apiClient.delete('/api/user/health-profile/photo'),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: ['health-profile']});
            toast.success('Profile photo removed');
        },
        onError: (err) => {
            toast.error(photoErrorMessage(err, 'Failed to remove profile photo'));
        },
    });

    const handlePhotoSelected = (file: File) => {
        if (!PHOTO_MIME_TYPES.includes(file.type)) {
            toast.error('Photo must be a JPEG, PNG, or WebP image');
            return;
        }
        if (file.size > PHOTO_MAX_BYTES) {
            toast.error('Photo must be 4MB or smaller');
            return;
        }
        setPhotoPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(file);
        });
        uploadPhoto.mutate(file);
    };

    const viewingUserId = currentPatient?.id ?? user?.userInfo?.id ?? null;
    const {data: latestBiometrics} = useLatestBiometrics(viewingUserId);
    const {data: biometricHistory} = useBiometricHistory(viewingUserId);

    const userInfo = user?.userInfo;
    const firstName = currentPatient?.firstName || userInfo?.firstName || '';
    const lastName = currentPatient?.lastName || userInfo?.lastName || '';
    // Prefer the live value from the health-profile endpoint (current DB row);
    // fall back to the cached auth userInfo when the profile hasn't loaded.
    const birthday = profile?.birthday ?? userInfo?.birthday;
    const sexRaw = (profile?.sex ?? userInfo?.gender ?? '').toLowerCase().trim();
    const gender = sexRaw.startsWith('m') ? 'Male' : sexRaw.startsWith('f') ? 'Female' : undefined;

    const height = latestBiometrics?.height ? parseFloat(latestBiometrics.height.value) : profile?.heightInches;
    const weight = latestBiometrics?.weight ? parseFloat(latestBiometrics.weight.value) : profile?.weightLbs;
    const waist = latestBiometrics?.waist ? parseFloat(latestBiometrics.waist.value) : profile?.waistInches;
    const bpSystolic = latestBiometrics?.bp_systolic
        ? parseFloat(latestBiometrics.bp_systolic.value)
        : profile?.bpSystolic;
    const bpDiastolic = latestBiometrics?.bp_diastolic
        ? parseFloat(latestBiometrics.bp_diastolic.value)
        : profile?.bpDiastolic;
    const bloodPressure = formatBloodPressure(bpSystolic, bpDiastolic);
    const bmiFromApi = latestBiometrics?.bmi ? parseFloat(latestBiometrics.bmi.value) : null;

    // Title reports when this data was taken — the most recent reading in the
    // region. Dates are ISO-prefixed, so a lexical max is also a chronological
    // one. Values that fell back to the health-profile row carry no date, so a
    // patient with only those (or with nothing) gets a bare "Biometric Data".
    const readingDates = TITLE_DATE_MEASURES.map((key) => latestBiometrics?.[key]?.recorded_on).filter(
        (d): d is string => Boolean(d)
    );
    const readingDate = readingDates.length
        ? formatBirthdate(
              readingDates.reduce((a, b) => (b > a ? b : a)),
              '2-digit'
          )
        : null;
    const biometricTitle = readingDate ? `Biometric Data (${readingDate})` : 'Biometric Data';
    const hasNoBiometrics = !height && !weight && !waist && !bpSystolic && !bpDiastolic;
    const bmi = bmiFromApi ?? (height && weight ? calculateBMI(height, weight) : null);

    // Three most recent readings per metric, oldest-first for the sparkline.
    // BMI derives from weight + latest height when stored BMI history is sparse,
    // matching the previous flip-card behavior.
    const {validatedTrends, patientTrends} = useMemo(() => {
        const history = biometricHistory as
            | Record<string, Array<{value: string; recorded_on: string; validated: boolean}>>
            | undefined;

        // Three most recent readings for one measurement on one side of the
        // flip (validated vs patient self-entered), oldest-first.
        const buildSeries = (key: string, validated: boolean): number[] =>
            (history?.[key] ?? [])
                .filter((e) => e.validated === validated)
                .slice(0, 3)
                .reverse()
                .map((e) => parseFloat(e.value))
                .filter((n) => !Number.isNaN(n));

        // BMI prefers stored readings; falls back to deriving from weight
        // history + the latest height when BMI isn't stored directly.
        const buildBmi = (validated: boolean): number[] => {
            const stored = buildSeries('bmi', validated);
            if (stored.length >= 1) return stored;
            const weights = (history?.weight ?? []).filter((e) => e.validated === validated);
            if (!height || weights.length < 1) return [];
            return weights
                .slice(0, 3)
                .reverse()
                .map((e) => {
                    const w = parseFloat(e.value);
                    return Number.isNaN(w) ? NaN : calculateBMI(height, w);
                })
                .filter((n) => !Number.isNaN(n));
        };

        const buildFace = (validated: boolean): TrendItem[] => {
            const items: TrendItem[] = [];

            const weight = buildSeries('weight', validated);
            if (weight.length >= 1) items.push({key: 'weight', label: 'Weight', values: weight});

            const bmi = buildBmi(validated);
            if (bmi.length >= 1) items.push({key: 'bmi', label: 'BMI', values: bmi});

            // Blood Pressure — one row carrying both series. Align the two on
            // their most-recent overlapping readings; fall back to systolic-only
            // if diastolic is sparse.
            const sys = buildSeries('bp_systolic', validated);
            const dia = buildSeries('bp_diastolic', validated);
            const m = Math.min(sys.length, dia.length);
            if (m >= 1) {
                items.push({
                    key: 'bp',
                    label: 'Blood Pressure',
                    values: sys.slice(sys.length - m),
                    secondary: dia.slice(dia.length - m),
                });
            } else if (sys.length >= 1) {
                items.push({key: 'bp', label: 'Blood Pressure', values: sys});
            }

            const waist = buildSeries('waist', validated);
            if (waist.length >= 1) items.push({key: 'waist', label: 'Waist', values: waist});

            return items;
        };

        return {validatedTrends: buildFace(true), patientTrends: buildFace(false)};
    }, [biometricHistory, height]);

    return (
        <section>
            {/* Page-section header */}
            <SectionHeader title="My Health Dashboard" />

            <div className="@container bg-white rounded-2xl border border-mld-panel-border shadow-sm overflow-hidden">
                {/* Reflows on the CARD's own width (container query), not the
                    viewport — the card is wide when full-bleed but narrow once
                    it shares a row with the BioMatch sidebar, so viewport
                    breakpoints can't capture it. Three states:
                      • < 48rem  → single column (mobile)
                      • 48–64rem → identity + biometric data side-by-side, with
                                   biometric history full-width on a new row
                      • ≥ 64rem  → full 3-column (the PSD layout) */}
                <div className="grid grid-cols-1 @3xl:grid-cols-2 @5xl:grid-cols-[24rem_minmax(0,1fr)_27rem]">
                    {/* Region 1 — identity */}
                    <div className="p-6 flex gap-4">
                        <Avatar
                            photoUrl={
                                isSelf ? (photoPreviewUrl ?? (resolveMediaUrl(profile?.profilePhotoUrl) || null)) : null
                            }
                            initials={getInitials(firstName, lastName) || 'P'}
                            canEdit={isSelf}
                            busy={uploadPhoto.isPending || removePhoto.isPending}
                            onSelectFile={handlePhotoSelected}
                            onRemove={() => removePhoto.mutate()}
                        />
                        <div className="flex flex-col min-w-0">
                            <h3 className="text-[24px] font-medium leading-tight text-mld-text-primary">
                                {firstName}
                                <br />
                                {lastName}
                            </h3>
                            {isSubscribed && (
                                <>
                                    <span className="mt-2 inline-flex w-fit items-center rounded-md border border-mld-primary/30 bg-mld-primary/15 px-4 py-1.5 text-[12px] font-medium text-mld-text-primary">
                                        My Labs+ Member
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setIsManagingMembership(true)}
                                        className="mt-1 w-fit inline-flex items-center gap-1 text-[12px] text-mld-muted-text hover:text-mld-text-primary transition-colors">
                                        Manage Membership
                                        <Icon name="arrow-right" className="h-3 w-3" />
                                    </button>
                                </>
                            )}
                            {/* Non-members get the same two-line treatment in neutral
                                gray, so the identity block never sits empty under the
                                name. Suppressed entirely where an admin has switched
                                My Labs Plus off for the org — see the module toggle in
                                Super Admin → Organizations → Modules.

                                Both strings are held to one line. This column is a
                                fixed 24rem grid track, so after the 160px avatar and
                                the card padding only ~160px is left — tighter pill
                                padding and no trailing arrow are what keep these
                                two labels from wrapping at that width. */}
                            {showFreeTier && (
                                <>
                                    <span className="mt-2 inline-flex w-fit items-center whitespace-nowrap rounded-md border border-gray-300 bg-gray-100 px-3 py-1.5 text-[12px] font-medium text-gray-700">
                                        Free Tier Membership
                                    </span>
                                    <Link
                                        to="/membership/benefits"
                                        className="mt-1 w-fit inline-flex items-center whitespace-nowrap text-[12px] text-mld-muted-text hover:text-mld-text-primary transition-colors">
                                        Explore Member Benefits
                                    </Link>
                                </>
                            )}
                            <dl className="mt-3 space-y-1 text-[14px]">
                                <div className="whitespace-nowrap">
                                    <dt className="inline text-gray-700">Date of Birth: </dt>
                                    <dd className="inline font-medium text-mld-text-primary">
                                        {birthday ? formatBirthdate(birthday, '2-digit') : '—'}
                                    </dd>
                                </div>
                                <div className="whitespace-nowrap">
                                    <dt className="inline text-gray-700">Biological Sex: </dt>
                                    <dd className="inline font-medium text-mld-text-primary">{gender ?? '—'}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>

                    {/* Region 2 — biometric data. Divider is an inset pseudo-element
                        (stops at the card padding) rather than a full-height border.
                        Flex column so the BMI pill can anchor to the bottom of the
                        region (see mt-auto below); the grid stretches this cell to
                        the row's height, so the slack lands above the pill. */}
                    <div className="flex flex-col p-6 border-t border-gray-200 @3xl:border-t-0 @3xl:relative @3xl:before:absolute @3xl:before:content-[''] @3xl:before:left-0 @3xl:before:top-6 @3xl:before:bottom-6 @3xl:before:w-px @3xl:before:bg-gray-200">
                        <div className="flex items-center gap-1.5 text-mld-text-primary">
                            <Icon name="stethoscope" className="h-5 w-5" />
                            <span className="text-base font-medium">{biometricTitle}</span>
                        </div>
                        <ul className="mt-3 space-y-1 text-[14px] text-mld-text-primary">
                            {/* With nothing on file every row is a dash, so the first
                                row carries the way to fix that — same modal as the
                                biometric-history tile's link. */}
                            <li className="flex items-center gap-3">
                                <span>Height: {formatMeasurement(height, formatHeight)}</span>
                                {hasNoBiometrics && (
                                    <TrackDataButton onClick={() => setIsEditing(true)} className="ml-auto" />
                                )}
                            </li>
                            <li>
                                <span>Weight: {formatMeasurement(weight, (w) => `${Math.round(w)} lb`)}</span>
                            </li>
                            <li>
                                <span>Waist: {formatMeasurement(waist, (w) => `${w} in`)}</span>
                            </li>
                            <li>
                                <span>Blood Pressure: {bloodPressure}</span>
                            </li>
                        </ul>
                        {bmi && (
                            // mt-auto pushes the pill to the bottom of the region;
                            // pt-4 keeps the original gap when there's no slack to
                            // absorb (single-column mobile, where height is content-driven).
                            <div className="mt-auto pt-4">
                                <div className="inline-flex w-fit items-center gap-3 rounded-lg border border-mld-primary/30 bg-mld-primary/15 px-5 py-1.5">
                                    <span className="text-[14px] font-medium text-mld-text-primary">Your BMI</span>
                                    <span className="text-[34px]  text-mld-primary leading-none tracking-tight">
                                        {bmi.toFixed(1)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Region 3 — biometric history. Inset pseudo-element divider.
                        Flips between validated readings (front) and the patient's
                        own self-reported entries (back). */}
                    <div className="relative p-6 border-t border-gray-200 @3xl:col-span-2 @5xl:col-span-1 @5xl:border-t-0 @5xl:before:absolute @5xl:before:content-[''] @5xl:before:left-0 @5xl:before:top-6 @5xl:before:bottom-6 @5xl:before:w-px @5xl:before:bg-gray-200">
                        {/* Flip toggle — lives outside the flipping element so it
                            stays put while the card rotates. */}
                        <button
                            type="button"
                            onClick={() => setIsFlipped((f) => !f)}
                            title={isFlipped ? 'Show validated history' : 'Show my entries'}
                            aria-label={isFlipped ? 'Show validated history' : 'Show my entries'}
                            className="absolute right-5 top-5 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full text-mld-muted-text transition-colors hover:bg-gray-100 hover:text-mld-text-primary">
                            <Icon name="repeat" className="h-4 w-4" />
                        </button>

                        <div className="[perspective:1200px]">
                            <div
                                className={clsx(
                                    'relative min-h-[190px] transition-transform duration-500 [transform-style:preserve-3d]',
                                    isFlipped && '[transform:rotateY(180deg)]'
                                )}>
                                {/* FRONT — validated */}
                                <div className="[backface-visibility:hidden]">
                                    <BiometricFace
                                        title="My Biometric History"
                                        note={
                                            <span
                                                className="inline-flex items-center gap-1 text-[11px] font-medium text-mld-primary"
                                                title="Verified by your care team">
                                                <Icon name="shield-check" className="h-3.5 w-3.5 shrink-0" />
                                                Verified
                                            </span>
                                        }
                                        trends={validatedTrends}
                                        emptyText="No verified readings on file yet."
                                    />
                                </div>
                                {/* BACK — patient self-reported */}
                                <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)]">
                                    <BiometricFace
                                        title="My Biometric History"
                                        note={
                                            <span
                                                className="inline-flex items-center gap-1 text-[11px] text-mld-muted-text"
                                                title="Not clinically validated">
                                                Self-reported
                                            </span>
                                        }
                                        trends={patientTrends}
                                        emptyText="Add measurements via Track Data to start your own trend."
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Track Data — moved inside the biometric-history tile
                            (was "Manage Profile" in the page-section header). */}
                        <TrackDataButton onClick={() => setIsEditing(true)} className="mt-3" />
                    </div>
                </div>
            </div>

            {isEditing && (
                <HealthProfileEditModal
                    profile={profile ?? {}}
                    history={biometricHistory}
                    heightLocked={Boolean(latestBiometrics?.height?.validated)}
                    onClose={() => setIsEditing(false)}
                />
            )}
            {isManagingMembership && (
                <ManageMembershipModal isSubscribed={isSubscribed} onClose={() => setIsManagingMembership(false)} />
            )}
        </section>
    );
};

export default HealthProfileCard;
